import { Link } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';

import { useWaitlistMode } from '../../lib/auth-context';

/**
 * JoinLink — the single "join WWAAVE" destination for every account CTA.
 *
 * Pre-launch, every CTA lands on the standalone early-access page
 * (Richie's ask: one page owns the capture, everything points at it). Once
 * `public_signups` flips to `all`, the same CTAs switch to `/sign-up` and carry
 * the role prefill. While the launch state is still resolving we assume the
 * pre-launch case — that's the current reality, and `/sign-up` renders its own
 * waitlist branch anyway, so an early click is never a dead end.
 *
 * Audience maps to the destination's own vocabulary: `host` →
 * `/earlyaccessvenue` (artist → `/earlyaccessartist`, the preset-toggle paths
 * Richie shares in comms), `?role=host` on sign-up.
 *
 * Renders a typed `<Link>` wrapping whatever the caller passes — the same
 * Link-wrapping-Button pattern used across the marketing surfaces, because
 * Mantine's polymorphic `component={Link}` erases TanStack's typed search
 * generics (see CLAUDE.md UI gotchas).
 */
export function JoinLink({
  audience,
  style,
  onClick,
  children,
}: {
  audience?: 'artist' | 'host';
  style?: CSSProperties;
  onClick?: () => void;
  children: ReactNode;
}) {
  const { signupsOpen, loaded } = useWaitlistMode();
  const linkStyle: CSSProperties = { textDecoration: 'none', ...style };
  const handlers = onClick ? { onClick } : {};

  if (loaded && signupsOpen) {
    if (audience === undefined) {
      return (
        <Link to="/sign-up" style={linkStyle} {...handlers}>
          {children}
        </Link>
      );
    }
    return (
      <Link
        to="/sign-up"
        search={(): { role: 'artist' | 'host' } => ({ role: audience })}
        style={linkStyle}
        {...handlers}
      >
        {children}
      </Link>
    );
  }

  if (audience === undefined) {
    return (
      <Link to="/earlyaccess" style={linkStyle} {...handlers}>
        {children}
      </Link>
    );
  }
  return (
    <Link
      to={audience === 'host' ? '/earlyaccessvenue' : '/earlyaccessartist'}
      style={linkStyle}
      {...handlers}
    >
      {children}
    </Link>
  );
}
